import { Suspense, lazy, useState, type FormEvent } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "react-router-dom";

import type { BodyMetric, NewBodyMetric } from "../../../shared/src";
import {
  addBodyMetric,
  ApiError,
  deleteBodyMetric,
  listBodyMetrics,
} from "../api-client";
import { ChartFallback } from "../components/ChartFallback";

const BodyweightChart = lazy(() =>
  import("../components/BodyweightChart").then((m) => ({
    default: m.BodyweightChart,
  })),
);

const BODY_METRICS_KEY = ["body-metrics"] as const;

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export function Bodyweight() {
  const queryClient = useQueryClient();
  const [weight, setWeight] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: BODY_METRICS_KEY,
    queryFn: () => listBodyMetrics({ limit: 100 }),
  });

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey: BODY_METRICS_KEY });
  }

  const addMutation = useMutation({
    mutationFn: (input: NewBodyMetric) => addBodyMetric(input),
    onSuccess: () => invalidate(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteBodyMetric(id),
    onSuccess: () => invalidate(),
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = Number(weight);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError("Enter a positive weight in kg.");
      return;
    }
    try {
      await addMutation.mutateAsync({
        body_weight_kg: parsed,
        notes: notes.trim() === "" ? null : notes.trim(),
      });
      setWeight("");
      setNotes("");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Something went wrong.",
      );
    }
  }

  return (
    <main className="min-h-dvh p-6 space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Bodyweight</h1>
        <Link
          to="/dashboard"
          className="text-sm text-slate-400 hover:text-slate-200"
        >
          ← Dashboard
        </Link>
      </header>

      <form
        onSubmit={onSubmit}
        className="rounded-lg border border-slate-800 bg-slate-900 p-4 space-y-3"
        aria-label="new bodyweight entry"
      >
        <label className="block">
          <span className="text-xs text-slate-400">Weight (kg)</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            aria-label="weight"
            className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-base"
          />
        </label>
        <label className="block">
          <span className="text-xs text-slate-400">Notes (optional)</span>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            aria-label="notes"
            className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-base"
          />
        </label>
        {error && (
          <p className="text-sm text-red-400" role="alert">
            {error}
          </p>
        )}
        <div className="flex items-center justify-end">
          <button
            type="submit"
            disabled={addMutation.isPending || weight.trim() === ""}
            className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
          >
            {addMutation.isPending ? "Logging…" : "Log weight"}
          </button>
        </div>
      </form>

      <section
        className="rounded-lg border border-slate-800 bg-slate-900 p-4 space-y-3"
        aria-label="bodyweight trend"
      >
        <h2 className="text-sm font-medium text-slate-300">Trend</h2>
        {listQuery.data && (
          <Suspense fallback={<ChartFallback />}>
            <BodyweightChart metrics={listQuery.data} />
          </Suspense>
        )}
      </section>

      <section className="space-y-2" aria-label="bodyweight history">
        <h2 className="text-sm font-medium text-slate-300">Recent entries</h2>
        {listQuery.data && listQuery.data.length === 0 && (
          <p className="text-sm text-slate-400">No entries yet.</p>
        )}
        <ul className="space-y-1">
          {listQuery.data?.map((m: BodyMetric) => (
            <li
              key={m.id}
              className="flex items-center justify-between gap-3 rounded-md border border-slate-800 bg-slate-900 p-3"
            >
              <div>
                <div className="text-base font-medium tabular-nums">
                  {m.body_weight_kg?.toFixed(1)} kg
                </div>
                <div className="text-xs text-slate-400">
                  {formatDateTime(m.measured_at)}
                  {m.notes && <span> · {m.notes}</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(m.id)}
                disabled={deleteMutation.isPending}
                aria-label={`delete entry ${m.id}`}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
